import moment from "moment";

/**
 * Validación de rangos de horario (día + hora inicio → día + hora fin).
 *
 * - Los días 1..7 (Lunes..Domingo) se proyectan sobre una línea de tiempo semanal
 *   en minutos: min = (dia - 1) * 1440 + hora*60 + minuto.
 * - Un rango cuyo fin queda "antes o igual" que el inicio en esa línea envuelve el
 *   fin de semana (Domingo -> Lunes).
 * - La comparación de solape es sobre intervalos cerrados [inicio, fin]: ambos
 *   extremos están ocupados, así que dos rangos que comparten aunque sea un único
 *   minuto se solapan, incluido el caso "el fin de uno == el inicio de otro". Para
 *   franjas consecutivas debe dejarse al menos 1 minuto de separación (p. ej.
 *   terminar 18:00 y empezar la siguiente 18:01).
 * - Los días 8 (Festivo) y 9 (Especial) no pertenecen a la semana: se validan en su
 *   propio "carril" y solo pueden solaparse contra otro rango del mismo día especial.
 */

export const OVERLAP_MESSAGE =
  "El rango de horario ingresado se sobrepone con uno ya existente. Por favor, ajuste los días y horas para mantener una secuencia sin conflictos.";

export const INCOHERENT_MESSAGE =
  "La configuración del tiempo no es válida: cuando el día inicial y el día final son el mismo, la hora de fin debe ser posterior a la hora de inicio.";

const MINUTES_PER_DAY = 1440;
const MINUTES_PER_WEEK = MINUTES_PER_DAY * 7;

const isSpecialDay = (dayId) => Number(dayId) === 8 || Number(dayId) === 9;

const minutesOfDay = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const m =
    typeof value === "string"
      ? moment(value, ["HH:mm:ss", "HH:mm"])
      : moment(value);
  if (!m || !m.isValid()) {
    return null;
  }
  return m.hours() * 60 + m.minutes();
};

/**
 * Descompone un rango en 1 o 2 sub-intervalos [start, end] etiquetados con un
 * "bucket" (semana o día especial). Devuelve [] si el rango está incompleto.
 */
const toSegments = (range) => {
  if (!range) {
    return [];
  }
  const startDay = Number(range.day);
  const endDay = Number(range.finalDay);
  const startMin = minutesOfDay(range.initialHour);
  const endMin = minutesOfDay(range.finalHour);
  if (!startDay || !endDay || startMin === null || endMin === null) {
    return [];
  }

  if (isSpecialDay(startDay) || isSpecialDay(endDay)) {
    if (endMin <= startMin) {
      return [];
    }
    return [{ bucket: `special-${startDay}`, start: startMin, end: endMin }];
  }

  const absStart = (startDay - 1) * MINUTES_PER_DAY + startMin;
  let absEnd = (endDay - 1) * MINUTES_PER_DAY + endMin;
  if (absEnd <= absStart) {
    absEnd += MINUTES_PER_WEEK;
  }
  if (absEnd - absStart >= MINUTES_PER_WEEK) {
    return [{ bucket: "week", start: 0, end: MINUTES_PER_WEEK }];
  }
  if (absEnd <= MINUTES_PER_WEEK) {
    return [{ bucket: "week", start: absStart, end: absEnd }];
  }
  return [
    { bucket: "week", start: absStart, end: MINUTES_PER_WEEK },
    { bucket: "week", start: 0, end: absEnd - MINUTES_PER_WEEK },
  ];
};

const segmentsOverlap = (a, b) =>
  a.bucket === b.bucket && a.start <= b.end && b.start <= a.end;

/**
 * true si el rango tiene una configuración de tiempo coherente (AC3).
 * - Mismo día (o día especial): la hora de fin debe ser estrictamente mayor.
 * - Días distintos: siempre coherente (puede cruzar de un día a otro o el fin de semana).
 */
export const isCoherentRange = (range) => {
  if (!range) {
    return false;
  }
  const startDay = Number(range.day);
  const endDay = Number(range.finalDay);
  const startMin = minutesOfDay(range.initialHour);
  const endMin = minutesOfDay(range.finalHour);
  if (!startDay || !endDay || startMin === null || endMin === null) {
    return false;
  }
  if (startDay === endDay || isSpecialDay(startDay)) {
    return endMin > startMin;
  }
  return true;
};

/**
 * true si `candidate` se solapa con algún rango de `list`
 * (se ignoran los marcados como deleted y el propio candidate).
 */
export const overlapsAny = (candidate, list = []) => {
  const candSegs = toSegments(candidate);
  if (candSegs.length === 0) {
    return false;
  }
  return list.some((other) => {
    if (!other || other === candidate || other.deleted) {
      return false;
    }
    const otherSegs = toSegments(other);
    return candSegs.some((cs) => otherSegs.some((os) => segmentsOverlap(cs, os)));
  });
};

/**
 * Valida una lista completa de rangos. Devuelve `null` si está OK, o el mensaje de
 * error correspondiente (incoherencia primero, luego solape).
 */
export const validateRanges = (ranges = []) => {
  const list = (ranges || []).filter((r) => r && !r.deleted);
  for (const range of list) {
    if (!isCoherentRange(range)) {
      return INCOHERENT_MESSAGE;
    }
  }
  for (let i = 0; i < list.length; i += 1) {
    const a = toSegments(list[i]);
    for (let j = i + 1; j < list.length; j += 1) {
      const b = toSegments(list[j]);
      if (a.some((as) => b.some((bs) => segmentsOverlap(as, bs)))) {
        return OVERLAP_MESSAGE;
      }
    }
  }
  return null;
};
